/**
 * SceneBuilder service
 * Handles creation of 3D scene elements (buildings/hangars, ground plane)
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { DoorSpec, DoorType } from '@/lib/templates/types'

// Building configuration for 3D rendering
export interface Building3DConfig {
  id: string
  name: string
  code?: string      // area_code (short label, e.g. "RMP-A")
  width: number      // feet
  depth: number      // feet
  height: number     // feet
  wallThickness?: number  // feet (default: 2)
  rotation?: number  // degrees
  position: [number, number, number]  // [x, y, z] in feet
  floorColor?: string
  wallColor?: string
  // Template info for template-aware rendering
  templateType?: 'single_unit_community' | 'single_row' | 'double_row' | 'outdoor_rectangular'
  typeConfig?: Record<string, unknown>  // Full type_config from database
  settings?: Record<string, unknown>    // Full settings from database
}

// Multi-unit building configuration
export interface MultiUnitBuildingConfig extends Building3DConfig {
  unitCount: number
  unitWidths: number[]        // Width of each unit in feet
  partitionThickness: number  // Partition wall thickness in feet
  doors?: DoorSpec[][]        // Doors for each unit (array of arrays)
}

export class SceneBuilder {
  /**
   * Create a building/hangar 3D group from configuration
   * @param config Building configuration
   * @param enableShadows Whether to enable shadows
   * @returns Three.js Group containing building geometry
   */
  static createBuilding(
    config: Building3DConfig,
    enableShadows: boolean = true
  ): THREE.Group {
    const group = new THREE.Group()
    group.name = `building-${config.id}`
    group.userData = { buildingId: config.id, buildingName: config.name }

    const wallThickness = config.wallThickness || 2

    // Convert dimensions from feet (config) to meters (Mapbox rendering)
    const widthMeters = config.width * FEET_TO_METERS
    const depthMeters = config.depth * FEET_TO_METERS
    const heightMeters = config.height * FEET_TO_METERS
    const thicknessMeters = wallThickness * FEET_TO_METERS

    const halfWidth = widthMeters / 2
    const halfDepth = depthMeters / 2
    const halfThickness = thicknessMeters / 2
    const halfHeight = heightMeters / 2

    // Floor - flat plane at ground level
    const floorGeometry = new THREE.PlaneGeometry(widthMeters, depthMeters)
    const floorColor = config.floorColor || '#cccccc'
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: floorColor,
      side: THREE.DoubleSide
    })
    const floor = new THREE.Mesh(floorGeometry, floorMaterial)
    floor.rotation.x = -Math.PI / 2 // Rotate to be horizontal
    floor.position.set(0, 0.01, 0) // Slightly above ground to avoid z-fighting
    floor.receiveShadow = enableShadows
    floor.name = 'floor'
    group.add(floor)

    // Wall material
    const wallColor = config.wallColor || '#e8e8e8'
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: wallColor,
      side: THREE.DoubleSide
    })

    // Back wall
    const backWallGeometry = new THREE.BoxGeometry(widthMeters, heightMeters, thicknessMeters)
    const backWall = new THREE.Mesh(backWallGeometry, wallMaterial)
    backWall.position.set(0, halfHeight, -halfDepth - halfThickness)
    backWall.receiveShadow = enableShadows
    backWall.castShadow = enableShadows
    backWall.name = 'backWall'
    group.add(backWall)

    // Left wall - extends to overlap with back wall
    const sideWallGeometry = new THREE.BoxGeometry(thicknessMeters, heightMeters, depthMeters + thicknessMeters)
    const leftWall = new THREE.Mesh(sideWallGeometry, wallMaterial)
    leftWall.position.set(-halfWidth - halfThickness, halfHeight, -halfThickness)
    leftWall.receiveShadow = enableShadows
    leftWall.castShadow = enableShadows
    leftWall.name = 'leftWall'
    group.add(leftWall)

    // Right wall - extends to overlap with back wall
    const rightWall = new THREE.Mesh(sideWallGeometry, wallMaterial)
    rightWall.position.set(halfWidth + halfThickness, halfHeight, -halfThickness)
    rightWall.receiveShadow = enableShadows
    rightWall.castShadow = enableShadows
    rightWall.name = 'rightWall'
    group.add(rightWall)

    // Apply building rotation (Y-axis rotation in degrees)
    const rotation = config.rotation || 0
    if (rotation !== 0) {
      group.rotation.y = (rotation * Math.PI) / 180 // Convert degrees to radians
    }

    // Apply building position (convert from feet to meters)
    group.position.set(
      config.position[0] * FEET_TO_METERS,
      config.position[1] * FEET_TO_METERS,
      config.position[2] * FEET_TO_METERS
    )

    return group
  }

  /**
   * Create an invisible ground plane for raycasting
   * @param extension Extension in feet (default 2000)
   * @returns Three.js Mesh for ground plane
   */
  static createGroundPlane(extension: number = 2000): THREE.Mesh {
    const extensionMeters = extension * FEET_TO_METERS

    const groundPlaneGeometry = new THREE.PlaneGeometry(extensionMeters * 2, extensionMeters * 2)
    const groundPlaneMaterial = new THREE.MeshBasicMaterial({
      visible: false, // Invisible - only for raycasting
      side: THREE.DoubleSide
    })
    const groundPlane = new THREE.Mesh(groundPlaneGeometry, groundPlaneMaterial)
    groundPlane.rotation.x = -Math.PI / 2 // Rotate to be horizontal
    groundPlane.position.y = 0 // At ground level
    groundPlane.name = 'groundPlane'

    return groundPlane
  }

  /**
   * Create a solid triangle arrowhead for rotation ring indicators
   * @param scale Scale multiplier (default 1 = 4m wide, 4m tall)
   */
  static createArrowShape(scale: number = 1): THREE.Shape {
    const s = scale
    const shape = new THREE.Shape()
    shape.moveTo(0, 3 * s)         // tip (pointing forward)
    shape.lineTo(-2 * s, -1 * s)   // back-left
    shape.lineTo(2 * s, -1 * s)    // back-right
    shape.closePath()
    return shape
  }

  /**
   * Set rotation on an arrow mesh to lay flat and point clockwise tangentially.
   * Uses quaternion multiplication to avoid Euler angle ordering issues.
   * @param mesh The arrow mesh
   * @param angle Position angle around the ring (radians from +Z)
   */
  static orientArrow(mesh: THREE.Mesh, angle: number): void {
    // qFlat: lay the XY-plane shape flat onto the XZ ground plane
    const qFlat = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(1, 0, 0), -Math.PI / 2
    )
    // qHeading: rotate around world Y so the arrow points clockwise tangentially
    const qHeading = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0), angle - Math.PI / 2
    )
    // Combine: lay flat first (qFlat), then rotate heading (qHeading)
    mesh.quaternion.multiplyQuaternions(qHeading, qFlat)
  }

  /**
   * Create a rotation ring with arrow indicators for rotation interaction
   * @param radius Ring radius in meters (default: 20 - larger for buildings)
   * @param tubeRadius Tube thickness in meters
   * @returns Three.js Group containing ring + 4 clockwise arrow chevrons
   */
  static createRotationRing(
    radius: number = 20,
    tubeRadius: number = 1.0
  ): THREE.Group {
    const group = new THREE.Group()
    group.name = 'rotationRing'

    // Ring torus
    const ringGeometry = new THREE.TorusGeometry(radius, tubeRadius, 8, 32)
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    })
    const ring = new THREE.Mesh(ringGeometry, ringMaterial)
    ring.rotation.x = -Math.PI / 2
    ring.name = 'ring-torus'
    group.add(ring)

    // 4 solid triangle arrows pointing clockwise around the ring
    const arrowShape = this.createArrowShape(radius * 0.075)
    const arrowGeom = new THREE.ShapeGeometry(arrowShape)
    const arrowMat = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
    })

    const arrowAngles = [0, Math.PI / 2, Math.PI, Math.PI * 3 / 2]
    for (const angle of arrowAngles) {
      const arrow = new THREE.Mesh(arrowGeom, arrowMat)
      this.orientArrow(arrow, angle)
      arrow.position.set(
        radius * Math.sin(angle),
        0.05,
        radius * Math.cos(angle)
      )
      arrow.name = 'rotation-arrow'
      group.add(arrow)
    }

    group.position.y = 0.5
    return group
  }

  /**
   * Setup scene lighting for outdoor environment
   * @param scene Three.js scene
   * @param enableShadows Whether to enable shadows
   * @param shadowQuality Shadow quality level
   */
  static setupLighting(
    scene: THREE.Scene,
    enableShadows: boolean = true,
    shadowQuality: 'low' | 'medium' | 'high' | 'ultra' = 'high'
  ): void {
    const shadowMapSizes = {
      low: 512,
      medium: 1024,
      high: 2048,
      ultra: 4096
    }

    // Ambient light provides base illumination
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2)
    scene.add(ambientLight)

    // Hemisphere light simulates outdoor lighting
    const hemisphereLight = new THREE.HemisphereLight(
      0xffffff, // Sky color
      0x888888, // Ground color
      1.0
    )
    hemisphereLight.position.set(0, 100, 0)
    scene.add(hemisphereLight)

    // Main directional light (sun)
    const directionalLight = new THREE.DirectionalLight(0xffffff, 2.5)
    directionalLight.position.set(100, 100, 50)
    directionalLight.castShadow = enableShadows

    if (enableShadows) {
      const shadowMapSize = shadowMapSizes[shadowQuality]
      directionalLight.shadow.mapSize.width = shadowMapSize
      directionalLight.shadow.mapSize.height = shadowMapSize
      directionalLight.shadow.camera.far = 500
      directionalLight.shadow.camera.left = -200
      directionalLight.shadow.camera.right = 200
      directionalLight.shadow.camera.top = 200
      directionalLight.shadow.camera.bottom = -200
    }
    scene.add(directionalLight)

    // Fill light from opposite side
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4)
    fillLight.position.set(-100, 80, -100)
    scene.add(fillLight)
  }

  /**
   * Setup renderer configuration for Mapbox integration
   * @param renderer Three.js WebGL renderer
   * @param enableShadows Whether to enable shadows
   */
  static setupRenderer(
    renderer: THREE.WebGLRenderer,
    enableShadows: boolean = true
  ): void {
    renderer.autoClear = false

    // Configure shadows
    renderer.shadowMap.enabled = enableShadows
    if (enableShadows) {
      renderer.shadowMap.type = THREE.PCFSoftShadowMap
    }

    // Enable tone mapping for realistic outdoor lighting
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.2
  }

  // ============================================================================
  // MULTI-UNIT BUILDING METHODS
  // ============================================================================

  /**
   * Create a partition wall (internal wall between units)
   * @param options Partition wall options
   * @param enableShadows Whether to enable shadows
   * @returns Three.js Mesh for partition wall
   */
  static createPartitionWall(
    options: {
      x: number           // X position in feet (building-local)
      depth: number       // Depth in feet
      height: number      // Height in feet
      thickness: number   // Thickness in feet
      color?: string
    },
    enableShadows: boolean = true
  ): THREE.Mesh {
    const {
      x,
      depth,
      height,
      thickness,
      color = '#b0b0b0', // Slightly darker than exterior walls
    } = options

    // Convert to meters
    const depthMeters = depth * FEET_TO_METERS
    const heightMeters = height * FEET_TO_METERS
    const thicknessMeters = thickness * FEET_TO_METERS
    const xMeters = x * FEET_TO_METERS

    const wallGeometry = new THREE.BoxGeometry(thicknessMeters, heightMeters, depthMeters)
    const wallMaterial = new THREE.MeshStandardMaterial({
      color,
      side: THREE.DoubleSide,
    })

    const wall = new THREE.Mesh(wallGeometry, wallMaterial)

    // Position: x is from building origin, y is half height up, z is centered
    wall.position.set(xMeters, heightMeters / 2, -depthMeters / 2)
    wall.receiveShadow = enableShadows
    wall.castShadow = enableShadows
    wall.name = 'partitionWall'

    return wall
  }

  /**
   * Create a multi-unit building with partition walls
   * @param config Multi-unit building configuration
   * @param enableShadows Whether to enable shadows
   * @returns Three.js Group containing building geometry
   */
  static createMultiUnitBuilding(
    config: MultiUnitBuildingConfig,
    enableShadows: boolean = true
  ): THREE.Group {
    const group = new THREE.Group()
    group.name = `building-${config.id}`
    group.userData = {
      buildingId: config.id,
      buildingName: config.name,
      unitCount: config.unitCount,
    }

    const wallThickness = config.wallThickness || 2
    const partitionThickness = config.partitionThickness || 0.5

    // Convert dimensions from feet to meters
    const widthMeters = config.width * FEET_TO_METERS
    const depthMeters = config.depth * FEET_TO_METERS
    const heightMeters = config.height * FEET_TO_METERS
    const thicknessMeters = wallThickness * FEET_TO_METERS

    const halfWidth = widthMeters / 2
    const halfDepth = depthMeters / 2
    const halfThickness = thicknessMeters / 2
    const halfHeight = heightMeters / 2

    // Floor
    const floorGeometry = new THREE.PlaneGeometry(widthMeters, depthMeters)
    const floorColor = config.floorColor || '#cccccc'
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: floorColor,
      side: THREE.DoubleSide,
    })
    const floor = new THREE.Mesh(floorGeometry, floorMaterial)
    floor.rotation.x = -Math.PI / 2
    floor.position.set(0, 0.01, 0)
    floor.receiveShadow = enableShadows
    floor.name = 'floor'
    group.add(floor)

    // Wall material
    const wallColor = config.wallColor || '#e8e8e8'
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: wallColor,
      side: THREE.DoubleSide,
    })

    // Back wall (north)
    const backWallGeometry = new THREE.BoxGeometry(widthMeters, heightMeters, thicknessMeters)
    const backWall = new THREE.Mesh(backWallGeometry, wallMaterial)
    backWall.position.set(0, halfHeight, -halfDepth - halfThickness)
    backWall.receiveShadow = enableShadows
    backWall.castShadow = enableShadows
    backWall.name = 'backWall'
    group.add(backWall)

    // Left wall (west)
    const sideWallGeometry = new THREE.BoxGeometry(thicknessMeters, heightMeters, depthMeters + thicknessMeters)
    const leftWall = new THREE.Mesh(sideWallGeometry, wallMaterial)
    leftWall.position.set(-halfWidth - halfThickness, halfHeight, -halfThickness)
    leftWall.receiveShadow = enableShadows
    leftWall.castShadow = enableShadows
    leftWall.name = 'leftWall'
    group.add(leftWall)

    // Right wall (east)
    const rightWall = new THREE.Mesh(sideWallGeometry, wallMaterial)
    rightWall.position.set(halfWidth + halfThickness, halfHeight, -halfThickness)
    rightWall.receiveShadow = enableShadows
    rightWall.castShadow = enableShadows
    rightWall.name = 'rightWall'
    group.add(rightWall)

    // Add partition walls between units
    // Building origin (0,0) is at center
    // Left edge is at -halfWidth, right edge is at +halfWidth
    let currentX = -halfWidth / FEET_TO_METERS + wallThickness // Start after left wall (in feet)

    for (let i = 0; i < config.unitCount - 1; i++) {
      // Move past current unit width
      currentX += config.unitWidths[i]

      // Create partition wall at this position
      const partitionWall = this.createPartitionWall(
        {
          x: currentX + partitionThickness / 2 - config.width / 2, // Relative to building center
          depth: config.depth,
          height: config.height,
          thickness: partitionThickness,
          color: '#a0a0a0', // Slightly different color for partitions
        },
        enableShadows
      )
      partitionWall.name = `partitionWall-${i}`
      group.add(partitionWall)

      // Move past partition
      currentX += partitionThickness
    }

    // Apply building rotation
    const rotation = config.rotation || 0
    if (rotation !== 0) {
      group.rotation.y = (rotation * Math.PI) / 180
    }

    // Apply building position
    group.position.set(
      config.position[0] * FEET_TO_METERS,
      config.position[1] * FEET_TO_METERS,
      config.position[2] * FEET_TO_METERS
    )

    return group
  }

  /**
   * Get door color based on door type
   */
  static getDoorColor(type: DoorType): string {
    const colors: Record<DoorType, string> = {
      bifold: '#4a5568',    // Gray-blue
      sliding: '#553c9a',   // Purple
      rolling: '#2b6cb0',   // Blue
      hydraulic: '#c53030', // Red
      manual: '#6b7280',    // Slate
    }
    return colors[type] || '#4a5568'
  }

  /**
   * Create door opening visualization (placeholder for door)
   * @param options Door options
   * @returns Three.js Mesh representing door opening
   */
  static createDoorOpening(
    options: {
      x: number        // X position in feet (from building origin)
      width: number    // Door width in feet
      height: number   // Door height in feet
      depth: number    // Wall depth in feet (for positioning)
      type: DoorType
    }
  ): THREE.Mesh {
    const { x, width, height, depth, type } = options

    // Convert to meters
    const widthMeters = width * FEET_TO_METERS
    const heightMeters = height * FEET_TO_METERS
    const xMeters = x * FEET_TO_METERS
    const depthMeters = depth * FEET_TO_METERS

    // Create a thin plane to represent door opening (visual indicator)
    const doorGeometry = new THREE.PlaneGeometry(widthMeters, heightMeters)
    const doorMaterial = new THREE.MeshBasicMaterial({
      color: this.getDoorColor(type),
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
    })

    const door = new THREE.Mesh(doorGeometry, doorMaterial)
    door.position.set(xMeters, heightMeters / 2, depthMeters / 2 + 0.01)
    door.name = `door-${type}`

    return door
  }
}
